import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { theme } from '../../../theme/theme';

const useInputClasses = makeStyles({
    root: {
        ':where(&)': {
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            ':where(& > label)': {
                ...theme.fonts.labelSemiBold,
            },
        },
    },
    outsideContainer: {
        ':where(&)': {
            display: 'flex',
            gap: 8,
        },
    },
    insideContainer: {
        ':where(&)': {
            display: 'flex',
            alignItems: 'center',
            overflow: 'auto',
            '@supports (overflow: overlay)': { '&': { overflow: 'overlay' } },
            flex: 1,
            gap: 10,
            padding: '14px 16px',
            borderRadius: 4,
            ...theme.fonts.label,
            transition: `all ${theme.transitions.default}s`,
            ':where(& > svg)': {
                minWidth: 20,
                minHeight: 20,
                width: 20,
                height: 20,
            },
        },
    },
    input: {
        ':where(&)': {
            flex: 1,
            width: 0,
            ...theme.fonts.label,
        },
    },
});

const useInputThemeClasses = makeStyles({
    root: {
        ':where(&)': {
            ':where(& > label)': {
                color: theme.colors.neutral[1],
            },
            ':where(& > .outside > .inside)': {
                color: theme.colors.neutral[1],
                ':where(& > svg)': { color: 'inherit' },
                ':where(&.open, &:focus-within)': { boxShadow: '0 0 0 1px currentColor inset' },
            },
            ':where(& > .outside > .inside > input)': {
                border: 'none',
                background: 'transparent',
                color: 'inherit',
                ':where(&::placeholder)': { color: 'inherit' },
                ':where(&:focus-visible)': { outline: 'none' },
                ':where(&:invalid)': { color: theme.colors.red[1] },
            },
        },
    },
    primary: {
        ':where(&)': {
            ':where(& > .outside > .inside)': {
                background: '#F1F5F6',
                color: '#283D4D',
                ':where(&.open, &:focus-within)': {
                    background: 'white',
                    boxShadow: '0 0 0 1px currentColor inset',
                },
                ':where(& > input::placeholder)': { color: '#51646C' },
            },
        },
    },
    light: {
        ':where(&)': {
            ':where(& > .outside > .inside)': {
                background: 'white',
                color: theme.colors.neutral[2],
                ':where(&.open, &:focus-within)': { boxShadow: 'none' },
                ':where(& > input::placeholder)': { color: theme.colors.neutral[3] },
            },
        },
    },
});

/**
 * Base input component with customizations for text based inputs.
 * It can be used with common input types such as `'text'`, `'number'`, `'email'`, etc.
 * It allows other components to be rendered within its tree so it can be further customized by other components.
 *
 * @param props.label the input label
 * @param props.left content rendered to the left of the input
 * @param props.right content rendered to the right of the input
 * @param props.bottom content rendered to the bottom of the input box
 * @param props.leftOutside content rendered to the left of the input box
 * @param props.rightOutside content rendered to the right of the input box
 * @param props.rootProps root container props
 * @param props.insideProps inside container props
 * @param props.outsideProps outside container props
 */
export const Input = (
    props: {
        theme?: keyof ReturnType<typeof useInputThemeClasses>;
        label?: string;
        left?: React.ReactNode;
        right?: React.ReactNode;
        bottom?: React.ReactNode;
        leftOutside?: React.ReactNode;
        rightOutside?: React.ReactNode;
        rootProps?: JSX.IntrinsicElements['div'];
        insideProps?: JSX.IntrinsicElements['div'];
        outsideProps?: JSX.IntrinsicElements['div'];
    } & JSX.IntrinsicElements['input'],
) => {
    const {
        theme,
        label,
        left,
        right,
        leftOutside,
        rightOutside,
        bottom,
        rootProps,
        outsideProps,
        insideProps,
        ...inputProps
    } = props;
    const classes = useInputClasses();
    const themeClasses = useInputThemeClasses();
    const id = React.useState(Math.random().toString())[0];
    const cls = { [themeClasses[props.theme ?? 'root']]: theme != undefined };

    return (
        <div {...rootProps} className={clsx(classes.root, themeClasses.root, cls, rootProps?.className)}>
            {label != undefined && <label htmlFor={id}>{label}</label>}
            <div {...outsideProps} className={clsx(classes.outsideContainer, outsideProps?.className, 'outside')}>
                {leftOutside}
                <div
                    {...insideProps}
                    className={clsx(classes.insideContainer, insideProps?.className, 'inside')}
                    onClick={e => {
                        if (!e.defaultPrevented) e.currentTarget.getElementsByTagName('input')[0].focus();
                        insideProps?.onClick?.(e);
                    }}
                >
                    {left}
                    <input {...inputProps} id={id} className={clsx(classes.input, inputProps.className)} />
                    {right}
                </div>
                {rightOutside}
            </div>
            {bottom}
        </div>
    );
};
